import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { loadLibrary, searchExercises, displayName } from "@/lib/exerciseLibrary";

// La lista completa son 1.324 ejercicios. Pintarlos todos a la vez cuesta
// medio segundo en móvil y nadie recorre esa lista entera, así que se muestran
// los primeros y se afina con el buscador.
const MAX_RESULTS = 60;

const chipClass = (active) =>
  `px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
    active
      ? "bg-primary text-primary-foreground shadow-sm"
      : "bg-secondary/40 text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
  }`;

export function ExerciseLibraryPicker({ onPick }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [query, setQuery] = useState("");
  const [bodyPart, setBodyPart] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadLibrary()
      .then((list) => {
        if (cancelled) return;
        setData(list);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bodyParts = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.map((e) => e.bodyPart))].filter(Boolean).sort();
  }, [data]);

  const results = useMemo(() => {
    if (!data) return [];
    return searchExercises(data, { query, bodyPart });
  }, [data, query, bodyPart]);

  if (status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-xs text-muted-foreground">Cargando biblioteca...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 px-6 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-muted-foreground">
          No se pudo cargar la biblioteca de ejercicios.
        </p>
      </div>
    );
  }

  const shown = results.slice(0, MAX_RESULTS);

  return (
    // min-w-0: sin esto la fila de filtros, al estar dentro de un grid, se
    // ensancha hasta caber entera en vez de hacer scroll, y arrastra consigo
    // el ancho del diálogo fuera de la pantalla en móvil.
    <div className="space-y-3 min-w-0">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar ejercicio o músculo..."
          autoFocus
          className="h-11 pl-9 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
        />
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        <button onClick={() => setBodyPart(null)} className={chipClass(bodyPart === null)}>
          Todos
        </button>
        {bodyParts.map((bp) => (
          <button key={bp} onClick={() => setBodyPart(bp)} className={chipClass(bodyPart === bp)}>
            {bp}
          </button>
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground/60 font-medium">
        {results.length === 0
          ? "No hay ejercicios que coincidan."
          : `${results.length} ejercicio${results.length === 1 ? "" : "s"}${
              results.length > MAX_RESULTS ? ` · mostrando ${MAX_RESULTS}` : ""
            }`}
      </p>

      <div className="space-y-1.5 max-h-[45vh] overflow-y-auto -mx-1 px-1">
        {shown.map((ex) => (
          <button
            key={ex.id}
            onClick={() => onPick(ex)}
            className="w-full flex items-center gap-3 p-2 rounded-2xl bg-secondary/20 hover:bg-secondary/50 transition-colors duration-200 text-left"
          >
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-secondary/40 flex-shrink-0">
              <img
                src={ex.gifUrl}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">{displayName(ex)}</p>
              <p className="text-[11px] text-muted-foreground truncate">
                {ex.muscleGroup} · {ex.equipment}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
